import { useState, useEffect } from "react";
import styles from "../../styles/addReceipt.module.css";
import { formatCurrency } from "../../../../../shared/utils/formatCurrency";
import apiFetch from "../../../../../shared/services/apiClient";

/** Mensaje de error inline bajo un campo */
function FieldError({ msg }) {
  if (!msg) return null;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 5,
        color: "#f87171",
        fontSize: "0.77rem",
        marginTop: 4,
        fontWeight: 500,
      }}
    >
      <i
        className="fa-solid fa-circle-exclamation"
        style={{ fontSize: "0.72rem" }}
      />
      {msg}
    </div>
  );
}

export function ReceiptDetails({
  receiptData,
  updateField,
  selectedFilial,
  setSelectedFilial,
  onDistribute,
  displayValue,
  setDisplayValue,
  readOnly = false,
  filialEditable = false,
  errors = {},
  clearFilialError,
  soloSaldoFavor = false,
  referenciaAutoSaldo = null,
}) {
  return (
    <div className={styles["receipt-details"]}>
      <ReceiptDetailsHeader />
      <ReceiptDetailsBody
        receiptData={receiptData}
        updateField={updateField}
        selectedFilial={selectedFilial}
        setSelectedFilial={setSelectedFilial}
        onDistributeAllMoney={onDistribute}
        displayValue={displayValue}
        setDisplayValue={setDisplayValue}
        readOnly={readOnly}
        filialEditable={filialEditable}
        errors={errors}
        clearFilialError={clearFilialError}
        soloSaldoFavor={soloSaldoFavor}
        referenciaAutoSaldo={referenciaAutoSaldo}
      />
    </div>
  );
}

function ReceiptDetailsHeader() {
  return (
    <div className={styles["receipt-details-header"]}>
      <h1>
        <i
          className={`fa-solid fa-money-bill ${styles["icon-receipt-details"]}`}
        />{" "}
        Detalles del Recibo
      </h1>
    </div>
  );
}

function ReceiptDetailsBody({
  receiptData,
  updateField,
  selectedFilial,
  setSelectedFilial,
  onDistributeAllMoney,
  displayValue,
  setDisplayValue,
  readOnly,
  errors,
  clearFilialError,
  soloSaldoFavor = false,
  referenciaAutoSaldo = null,
  filialEditable
}) {
  const [filiales, setFiliales] = useState([]);
  const [cuentaBancarias, setCuentaBancarias] = useState([]);

  useEffect(() => {
    async function fetchFiliales() {
      try {
        const res = await apiFetch("/api/residents/filial/");
        const data = await res.json();
        setFiliales(data.results ?? data ?? []);
      } catch {}
    }
    async function fetchCuentaBancaria() {
      try {
        const res = await apiFetch("/api/finance/cuentaBancaria/");
        const data = await res.json();
        setCuentaBancarias(data.results ?? []);
      } catch {}
    }
    fetchFiliales();
    fetchCuentaBancaria();
  }, []);

  // Auto-seleccionar primeros valores cuando NO es readOnly
  useEffect(() => {
    if (readOnly) return;
    if (filiales.length > 0 && selectedFilial === 0) {
      setSelectedFilial(Number(filiales[0].id));
    }
    if (cuentaBancarias.length > 0 && receiptData.cuenta_bancaria === 0) {
      updateField("cuenta_bancaria", cuentaBancarias[0].id);
    }
  }, [
    filiales,
    selectedFilial,
    cuentaBancarias,
    receiptData.cuenta_bancaria,
    readOnly,
  ]);

  const errorBorder = (field) =>
    errors[field] ? { borderColor: "#ef4444" } : undefined;

  return (
    <div className={styles["receipt-details-body"]}>
      {/* ── Fila 1: Fecha + Filial ───────────────────────────────── */}
      <div className="row">
        <div className="col">
          <div className="form-group">
            <label className="form-label">
              Fecha de Pago <span style={{ color: "#f87171" }}>*</span>
            </label>
            <input
              className="form-control"
              type="date"
              value={receiptData.fecha_pago}
              onChange={(e) => updateField("fecha_pago", e.target.value)}
              disabled={readOnly}
              style={errorBorder("fecha_pago")}
            />
            <FieldError msg={errors.fecha_pago} />
          </div>
        </div>

        <div className="col">
          <div className="form-group">
            <label className="form-label">
              Filial <span style={{ color: "#f87171" }}>*</span>
            </label>
            {filiales.length === 0 ? (
              <div
                style={{
                  background: "rgba(239,68,68,0.08)",
                  border: "1px solid rgba(239,68,68,0.3)",
                  borderRadius: 6,
                  padding: "8px 12px",
                  color: "#f87171",
                  fontSize: "0.82rem",
                  display: "flex",
                  alignItems: "center",
                  gap: 7,
                }}
              >
                <i className="fa-solid fa-triangle-exclamation" />
                No hay filiales. Agregá un residente primero.
              </div>
            ) : (
              <>
                <select
                  className="form-select"
                  value={selectedFilial}
                  onChange={(e) => {
                    setSelectedFilial(Number(e.target.value));
                    if (clearFilialError) clearFilialError();
                  }}
                  disabled={readOnly && !filialEditable}
                  style={errorBorder("filial")}
                >
                  {filiales.map((fil) => (
                    <option value={fil.id} key={fil.id}>
                      {fil.idFilial}
                    </option>
                  ))}
                </select>
                <FieldError msg={errors.filial} />
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── Fila 2: Monto ────────────────────────────────────────── */}
      <div className="row">
        <div className="form-group">
          <label className="form-label">
            Monto Total <span style={{ color: "#f87171" }}>*</span>
          </label>
          <div className="d-flex" style={{ flexDirection: "column" }}>
            <div className="d-flex">
              <input
                className="form-control"
                type="text"
                value={displayValue}
                placeholder="0,00"
                disabled={readOnly}
                style={errorBorder("monto")}
                onFocus={() => {
                  if (readOnly) return;
                  setDisplayValue(
                    receiptData.monto
                      ? receiptData.monto.toString().replace(".", ",")
                      : "",
                  );
                }}
                onChange={(e) => {
                  if (readOnly) return;
                  let raw = e.target.value.replace(/[^0-9,]/g, "");
                  const parts = raw.split(",");
                  if (parts.length > 2) return;
                  if (parts[1]?.length > 2) return;
                  const num = Number(raw.replace(",", ".")) || 0;
                  setDisplayValue(raw);
                  updateField("monto", Math.min(num, 999999999999999));
                }}
                onBlur={() => {
                  if (readOnly) return;
                  setDisplayValue(
                    receiptData.monto > 0
                      ? formatCurrency(receiptData.monto)
                      : "",
                  );
                }}
              />
              <button
                className="btn btn-primary"
                onClick={onDistributeAllMoney}
              >
                Distribuir
              </button>
            </div>
            <FieldError msg={errors.monto} />
          </div>
        </div>
      </div>

      {/* ── Fila 3: Método + Cuenta bancaria ────────────────────── */}
      <div className="row">
        <div className="col">
          <div className="form-group">
            <label className="form-label">Medio de pago</label>
            <select
              className="form-select"
              value={receiptData.metodo_pago}
              onChange={(e) => updateField("metodo_pago", e.target.value)}
              disabled={readOnly}
            >
              <option value="TRANSFERENCIA">Transferencia</option>
              <option value="EFECTIVO">Efectivo</option>
              <option value="CHEQUE">Cheque</option>
            </select>
          </div>
        </div>

        <div className="col">
          <div className="form-group">
            <label className="form-label">
              Cuenta Bancaria <span style={{ color: "#f87171" }}>*</span>
            </label>
            {cuentaBancarias.length === 0 ? (
              <div
                style={{
                  background: "rgba(239,68,68,0.08)",
                  border: "1px solid rgba(239,68,68,0.3)",
                  borderRadius: 6,
                  padding: "8px 12px",
                  color: "#f87171",
                  fontSize: "0.82rem",
                  display: "flex",
                  alignItems: "center",
                  gap: 7,
                }}
              >
                <i className="fa-solid fa-triangle-exclamation" />
                No hay cuentas bancarias. Configuralas en Settings.
              </div>
            ) : (
              <>
                <select
                  className="form-select"
                  value={receiptData.cuenta_bancaria}
                  onChange={(e) =>
                    updateField("cuenta_bancaria", Number(e.target.value))
                  }
                  disabled={readOnly}
                  style={errorBorder("cuenta_bancaria")}
                >
                  {cuentaBancarias.map((cta) => (
                    <option value={cta.id} key={cta.id}>
                      {cta.banco} — {cta.numero_cuenta}
                    </option>
                  ))}
                </select>
                <FieldError msg={errors.cuenta_bancaria} />
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── Fila 4: Referencia bancaria ───────────────────────────── */}
      <div className="row">
        <div className="form-group">
          <label className="form-label">
            Referencia Bancaria{" "}
            {soloSaldoFavor
              ? <span style={{ color:"#f97316", fontSize:"0.78rem", fontWeight:400 }}>(automática — solo saldo a favor)</span>
              : <span style={{ color: "#f87171" }}>*</span>}
          </label>
          <input
            className="form-control"
            type="text"
            value={soloSaldoFavor ? (referenciaAutoSaldo || "Aplicación saldo a favor") : receiptData.documento}
            onChange={(e) => { if (!soloSaldoFavor) updateField("documento", e.target.value); }}
            disabled={readOnly || soloSaldoFavor}
            placeholder={soloSaldoFavor ? "Se usa la referencia del saldo a favor" : "N° de transferencia, cheque, etc."}
            style={{
              ...errorBorder("documento"),
              ...(soloSaldoFavor ? { background:"rgba(249,115,22,0.06)", borderColor:"rgba(249,115,22,0.3)", color:"#f97316", cursor:"not-allowed" } : {})
            }}
          />
          {soloSaldoFavor && (
            <div style={{ fontSize:"0.76rem", color:"#f97316", marginTop:4, display:"flex", alignItems:"center", gap:4 }}>
              <i className="fa-solid fa-circle-info"/>
              La referencia se toma automáticamente del recibo del saldo a favor
            </div>
          )}
          <FieldError msg={errors.documento} />
        </div>
      </div>

      {/* ── Fila 5: Descripción (anexo bancario) ─────────────────── */}
      <div className="row">
        <div className="form-group">
          <label className="form-label">
            Descripción <span style={{ color:"#6b7280", fontWeight:400 }}>(opcional)</span>
          </label>
          <input
            className="form-control"
            type="text"
            value={receiptData.descripcion ?? ""}
            onChange={(e) => updateField("descripcion", e.target.value)}
            disabled={readOnly}
            placeholder="Descripción para el anexo bancario"
          />
          <div style={{ fontSize:"0.73rem", color:"#6b7280", marginTop:3 }}>
            Si se deja vacía, se usa el N° de recibo como descripción.
          </div>
        </div>
      </div>
    </div>
  );
}
